import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import {
  ACTOR_ID_HEADER_KEY,
  AUTHORIZATION_HEADER_KEY,
  PROFILE_SERVICE_BASE_URL,
} from "constants";
import { useAuth } from "contexts/AuthContext";
import apiClient from "utils/apiClient";

const API_BASE_URL = `${PROFILE_SERVICE_BASE_URL}/api/v1/dial-memberships`;

export default function useDialMemberships() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getRequestHeaders = useCallback(() => {
    const token = sessionStorage.getItem("authToken");
    return {
      [ACTOR_ID_HEADER_KEY]: user?.id || "",
      [AUTHORIZATION_HEADER_KEY]: token ? `Bearer ${token}` : "",
    };
  }, [user?.id]);

  const getApplicationsForDial = useCallback(async (dialId) => {
    if (!dialId) return [];
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(
        `${API_BASE_URL}/dial/${dialId}/applications`,
        {
          headers: getRequestHeaders(),
        }
      );
      return Array.isArray(res.data) ? res.data : res.data?.content || [];
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to fetch dial applications";
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [getRequestHeaders]);

  const addApplicationToDial = useCallback(
    async (dialId, appId, enabled = true) => {
      if (!dialId || !appId) return null;
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.post(
          `${API_BASE_URL}/dial/${dialId}/application/${appId}`,
          null,
          {
            params: { enabled },
            headers: getRequestHeaders(),
          }
        );
        return res.data;
      } catch (err) {
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to add application to dial";
        setError(message);
        toast.error(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getRequestHeaders]
  );

  const bulkAddApplications = useCallback(
    async (dialId, appIds, enabled = true) => {
      if (!dialId || !Array.isArray(appIds) || appIds.length === 0) return [];
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.post(
          `${API_BASE_URL}/dial/${dialId}/bulk`,
          appIds,
          {
            params: { enabled },
            headers: getRequestHeaders(),
          }
        );
        return Array.isArray(res.data) ? res.data : res.data?.content || [];
      } catch (err) {
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to add applications to dial";
        setError(message);
        toast.error(message);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [getRequestHeaders]
  );

  const removeApplicationFromDial = useCallback(async (dialId, appId) => {
    if (!dialId || !appId) return;
    setLoading(true);
    setError(null);
    try {
      await apiClient.delete(
        `${API_BASE_URL}/dial/${dialId}/application/${appId}`,
        {
          headers: getRequestHeaders(),
        }
      );
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to remove application from dial";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [getRequestHeaders]);

  const syncDialApplications = useCallback(
    async (dialId, nextAppIds = [], currentAppIds = []) => {
      if (!dialId) return;
      const nextSet = new Set((nextAppIds || []).map(String));
      const currentSet = new Set((currentAppIds || []).map(String));

      const toAdd = Array.from(nextSet).filter((id) => !currentSet.has(id));
      const toRemove = Array.from(currentSet).filter((id) => !nextSet.has(id));

      if (toAdd.length) {
        await bulkAddApplications(dialId, toAdd, true);
      }

      if (toRemove.length) {
        await Promise.all(
          toRemove.map((id) => removeApplicationFromDial(dialId, id))
        );
      }
    },
    [bulkAddApplications, removeApplicationFromDial]
  );

  return {
    loading,
    error,
    getApplicationsForDial,
    addApplicationToDial,
    bulkAddApplications,
    removeApplicationFromDial,
    syncDialApplications,
  };
}
